import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  orderBy,
  writeBatch,
  deleteDoc,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { GuardData } from '../../manage_guard/service/guardService';

export type AttendanceStatus = 'present' | 'absent' | 'leave' | 'undeployed';

export interface DayAttendance {
  date: string; // YYYY-MM-DD format
  status: AttendanceStatus;
  markedAt?: Date;
  markedBy?: string;
}

export interface MonthlyAttendance {
  guardId: string;
  year: number;
  month: number; // 1-12
  days: { [date: string]: AttendanceStatus }; // date as key, status as value
  updatedAt?: Date;
  paymentStatus?: 'paid' | 'unpaid'; // Payment status for the month
  paidAt?: Date; // Date when payment was made
  // Calculated fields
  presentDays?: number;
  absentDays?: number;
  leaveDays?: number;
  salaryTillToday?: number; // For current month
  calculatedSalary?: number; // Full salary for past months
  monthlySalary?: number; // Guard's monthly salary at time of calculation
  otDays?: number; // Number of OT days
  // Salary payment fields
  advanceSalary?: number; // Advance salary given to guard
  balance?: number; // Outstanding balance
  balanceReason?: string; // Reason if balance is cleared/zero
  paidAmount?: number; // Amount paid in this payment cycle
  deductionAmount?: number; // Deduction amount for this month
  lastPaymentDate?: Date; // Date of last payment
}

export interface AttendanceSummary {
  totalDays: number;
  present: number;
  absent: number;
  leave: number;
  undeployed: number;
  monthlySalary: number;
  salaryTillToday: number;
  ot?: number;
  advanceSalary?: number;
  balance?: number;
  openingBalance?: number;
}

export interface SalaryBreakdownPeriod {
  clientName: string;
  branchCode: string;
  startDate: string;
  endDate: string;
  salary: number;
  daysInMonth: number;
  dailyRate: number;
  presentDays: number;
  calculatedSalary: number;
  isOT?: boolean;
}

export interface AttendanceHistory {
  year: number;
  month: number;
  totalDays: number;
  present: number;
  absent: number;
  leave: number;
  ot?: number;
  calculatedSalary: number;
  paymentStatus: 'paid' | 'unpaid' | 'current'; // 'current' for current month
  advanceSalary?: number;
  balance?: number;
  balanceReason?: string;
}

/**
 * Get the number of days in a specific month
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

/**
 * Get current date info
 */
export const getCurrentDateInfo = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1, // 1-12
    day: now.getDate(),
  };
};

/**
 * Format date to YYYY-MM-DD
 */
export const formatDate = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Get monthly attendance for a guard
 * Returns attendance records for a specific month
 */
export const getMonthlyAttendance = async (
  guardId: string,
  year: number,
  month: number,
  companyId?: string
): Promise<MonthlyAttendance> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const docRef = doc(db, 'companies', uid, 'guardAttendance', guardId, 'months', monthKey);

  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as MonthlyAttendance;
  }

  // Return empty attendance record if not exists
  return {
    guardId,
    year,
    month,
    days: {},
  };
};

/**
 * Mark attendance for a specific day
 * Now saves all statuses including 'present' to ensure history records exist
 */
export const markAttendance = async (
  guardId: string,
  date: string, // YYYY-MM-DD
  status: AttendanceStatus,
  companyId?: string,
  guard?: GuardData // Optional guard data for salary calculation
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) {
    console.error('markAttendance: User not authenticated');
    throw new Error('User not authenticated');
  }

  const [year, month] = date.split('-').map(Number);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const docRef = doc(db, 'companies', uid, 'guardAttendance', guardId, 'months', monthKey);

  console.log('markAttendance:', {
    guardId,
    date,
    status,
    companyId: uid,
    monthKey,
    path: `companies/${uid}/guardAttendance/${guardId}/months/${monthKey}`
  });

  try {
    // Get existing data
    const docSnap = await getDoc(docRef);
    let attendanceData: MonthlyAttendance;

    if (docSnap.exists()) {
      attendanceData = docSnap.data() as MonthlyAttendance;
      console.log('markAttendance: Found existing document', attendanceData);
    } else {
      attendanceData = {
        guardId,
        year,
        month,
        days: {},
      };
      console.log('markAttendance: Creating new document', attendanceData);
    }

    // Update the specific day - now save all statuses including 'present'
    attendanceData.days[date] = status;
    attendanceData.updatedAt = new Date();

    // Calculate and update summary fields if guard data is provided
    if (guard) {
      const summary = await calculateMonthlySummary(guard, year, month, companyId);
      const currentDate = getCurrentDateInfo();
      const isCurrentMonth = year === currentDate.year && month === currentDate.month;

      attendanceData.presentDays = summary.present;
      attendanceData.absentDays = summary.absent;
      attendanceData.leaveDays = summary.leave;
      attendanceData.monthlySalary = summary.monthlySalary;

      if (isCurrentMonth) {
        // For current month, save salary till today
        attendanceData.salaryTillToday = summary.salaryTillToday;
      } else {
        // For past months, save full calculated salary
        const totalDaysInMonth = getDaysInMonth(year, month);
        const calculatedSalary = totalDaysInMonth > 0 && summary.monthlySalary > 0
          ? (summary.monthlySalary / totalDaysInMonth) * (summary.present + summary.leave)
          : 0;
        attendanceData.calculatedSalary = isNaN(calculatedSalary) ? 0 : Math.round(calculatedSalary);
      }
    }

    console.log('markAttendance: Saving data', {
      ...attendanceData,
      daysCount: Object.keys(attendanceData.days).length
    });

    // Save to Firestore
    await setDoc(docRef, attendanceData, { merge: false });
    console.log('markAttendance: Successfully saved to Firestore');
  } catch (error: any) {
    console.error('markAttendance: Error saving to Firestore', {
      error,
      code: error.code,
      message: error.message,
      guardId,
      date,
      status,
      path: `companies/${uid}/guardAttendance/${guardId}/months/${monthKey}`
    });
    throw error;
  }
};

const parseDate = (dateVal: any): Date | null => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  if (typeof dateVal.toDate === 'function') return dateVal.toDate();
  if (dateVal.seconds) return new Date(dateVal.seconds * 1000);

  // Handle string formats
  if (typeof dateVal === 'string') {
    const dmyMatch = dateVal.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmyMatch) {
      return new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
    }
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Helper to calculate pro-rated salary based on deployment history
 */
export const calculateProRatedSalary = (
  guard: GuardData,
  year: number,
  month: number,
  attendanceDays: { [date: string]: AttendanceStatus },
  endDay: number
): { finalSalary: number; currentMonthlySalary: number } => {
  const totalDaysInMonth = getDaysInMonth(year, month);
  let salaryTillToday = 0;

  // Helper to normalize dates for comparison (Y-M-D only)
  const normalizeToDay = (d: Date | null | undefined): number | null => {
    if (!d) return null;
    const normalized = new Date(d);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  };

  // Collect all deployment periods
  const deploymentPeriods: { start: Date; end: Date | null; salary?: number | string }[] = [];

  if (guard.currentDeployment?.postDate) {
    const start = parseDate(guard.currentDeployment.postDate);
    const end = parseDate(guard.currentDeployment.endDate);
    if (start) {
      deploymentPeriods.push({
        start,
        end,
        salary: guard.currentDeployment.salary
      });
    }
  }

  if (guard.currentOTDeployment?.postDate) {
    const start = parseDate(guard.currentOTDeployment.postDate);
    const end = parseDate(guard.currentOTDeployment.endDate);
    if (start) {
      deploymentPeriods.push({
        start,
        end,
        salary: guard.currentOTDeployment.salary
      });
    }
  }

  if (guard.history && Array.isArray(guard.history)) {
    guard.history.forEach((h) => {
      const start = parseDate(h.postDate);
      const end = parseDate(h.endDate) || parseDate(h.completedAt);
      if (start) {
        deploymentPeriods.push({
          start,
          end,
          salary: h.salary
        });
      }
    });
  }

  // Calculate day-by-day
  for (let d = 1; d <= endDay; d++) {
    const testDate = new Date(year, month - 1, d);
    testDate.setHours(12, 0, 0, 0); // Use noon to avoid TZ edge cases
    const testTime = testDate.getTime();

    const matchingPeriods = deploymentPeriods.filter((period) => {
      const periodStart = new Date(period.start);
      periodStart.setHours(0, 0, 0, 0);
      const periodStartTime = periodStart.getTime();

      const periodEnd = period.end ? new Date(period.end) : null;
      if (periodEnd) periodEnd.setHours(23, 59, 59, 999);
      const periodEndTime = periodEnd ? periodEnd.getTime() : Infinity;

      return testTime >= periodStartTime && testTime <= periodEndTime;
    });

    if (matchingPeriods.length === 0) continue;

    const dateKey = formatDate(year, month, d);
    const status = attendanceDays[dateKey];

    // Skip if not present or leave
    if (status && status !== 'present' && status !== 'leave') continue;

    matchingPeriods.forEach((period) => {
      // Use salary from period snapshot, fallback to guard's top-level salary
      const salaryToParse = (period.salary !== undefined && period.salary !== 0)
        ? period.salary
        : guard.salary;

      let dailySalary = 0;
      if (typeof salaryToParse === 'number') {
        dailySalary = salaryToParse;
      } else if (typeof salaryToParse === 'string') {
        dailySalary = parseFloat(salaryToParse.replace(/[^0-9.]/g, '')) || 0;
      }

      salaryTillToday += totalDaysInMonth > 0 ? (dailySalary / totalDaysInMonth) : 0;
    });
  }

  // Monthly Salary for display: Sum active currentDeployment and currentOTDeployment
  let currentMonthlySalaryVal = 0;
  let hasActiveDeployment = false;

  if (guard.currentDeployment?.salary !== undefined && guard.currentDeployment?.salary !== 0) {
    const s = typeof guard.currentDeployment.salary === 'number'
      ? guard.currentDeployment.salary
      : parseFloat(String(guard.currentDeployment.salary).replace(/[^0-9.]/g, '')) || 0;
    currentMonthlySalaryVal += s;
    hasActiveDeployment = true;
  }

  if (guard.currentOTDeployment?.salary !== undefined && guard.currentOTDeployment?.salary !== 0) {
    const s = typeof guard.currentOTDeployment.salary === 'number'
      ? guard.currentOTDeployment.salary
      : parseFloat(String(guard.currentOTDeployment.salary).replace(/[^0-9.]/g, '')) || 0;
    currentMonthlySalaryVal += s;
    hasActiveDeployment = true;
  }

  // If no active deployments, check history entries (for terminated deployments)
  if (!hasActiveDeployment && guard.history && Array.isArray(guard.history) && guard.history.length > 0) {
    // Get the most recent history entry that overlaps with this month
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    for (const h of guard.history) {
      const historyStart = parseDate(h.postDate);
      const historyEnd = parseDate(h.endDate) || parseDate(h.completedAt);

      if (historyStart && historyEnd) {
        // Check if this history entry overlaps with the selected month
        if (historyStart <= monthEnd && historyEnd >= monthStart) {
          const historySalary = h.salary;
          if (historySalary !== undefined && historySalary !== 0) {
            currentMonthlySalaryVal = typeof historySalary === 'number'
              ? historySalary
              : parseFloat(String(historySalary).replace(/[^0-9.]/g, '')) || 0;
            break; // Use the first matching history entry
          }
        }
      }
    }
  }
  // Finally fall back to guard's top-level salary if still 0
  if (currentMonthlySalaryVal === 0 && guard.salary) {
    currentMonthlySalaryVal = typeof guard.salary === 'number'
      ? guard.salary
      : parseFloat(String(guard.salary).replace(/[^0-9.]/g, '')) || 0;
  }

  return {
    finalSalary: Math.round(salaryTillToday),
    currentMonthlySalary: currentMonthlySalaryVal
  };
};

/**
 * Calculate monthly attendance summary
 * Days without records are considered present by default
 * Only counts days within deployment period if guard is deployed
 */
export const calculateMonthlySummary = async (
  guard: GuardData,
  year: number,
  month: number,
  companyId?: string,
  endDate?: Date
): Promise<AttendanceSummary> => {
  const attendance = await getMonthlyAttendance(guard.id, year, month, companyId);
  const totalDaysInMonth = getDaysInMonth(year, month);
  const currentDate = getCurrentDateInfo();

  // Collect all deployment periods (current and history)
  const deploymentPeriods: { start: Date; end: Date | null }[] = [];

  // Helper function to parse dates from various formats (Timestamps, ISO, and DD/MM/YYYY)
  const parseDate = (dateVal: any): Date | null => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal.toDate === 'function') return dateVal.toDate();
    if (dateVal.seconds) return new Date(dateVal.seconds * 1000);

    // Handle string formats
    if (typeof dateVal === 'string') {
      const dmyMatch = dateVal.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dmyMatch) {
        return new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
      }
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  // Add current deployment
  if (guard.currentDeployment?.postDate) {
    const start = parseDate(guard.currentDeployment.postDate);
    const end = parseDate(guard.currentDeployment.endDate);
    if (start) {
      deploymentPeriods.push({ start, end });
    }
  }

  // Add current OT deployment
  if (guard.currentOTDeployment?.postDate) {
    const start = parseDate(guard.currentOTDeployment.postDate);
    const end = parseDate(guard.currentOTDeployment.endDate);
    if (start) {
      deploymentPeriods.push({ start, end });
    }
  }

  // Add history deployments
  if (guard.history && Array.isArray(guard.history)) {
    guard.history.forEach((h) => {
      const start = parseDate(h.postDate);
      const end = parseDate(h.endDate) || parseDate(h.completedAt);
      if (start) {
        deploymentPeriods.push({ start, end });
      }
    });
  }

  // Separate OT periods for summary counting
  const otPeriods: { start: Date; end: Date | null }[] = [];
  if (guard.currentOTDeployment?.postDate) {
    const start = parseDate(guard.currentOTDeployment.postDate);
    const end = parseDate(guard.currentOTDeployment.endDate);
    if (start) otPeriods.push({ start, end });
  }
  // Also check history for OT records (slotNumber > 100)
  if (guard.history && Array.isArray(guard.history)) {
    guard.history.forEach((h: any) => {
      if ((h.slotNumber || 0) > 100) {
        const start = parseDate(h.postDate);
        const end = parseDate(h.endDate) || parseDate(h.completedAt);
        if (start) otPeriods.push({ start, end });
      }
    });
  }

  // Helper to check if a day is within ANY deployment period
  const isDateInDeployment = (d: number): boolean => {
    const testDate = new Date(year, month - 1, d);
    testDate.setHours(0, 0, 0, 0);

    return deploymentPeriods.some((period) => {
      const periodStart = new Date(period.start);
      periodStart.setHours(0, 0, 0, 0);

      const periodEnd = period.end ? new Date(period.end) : null;
      if (periodEnd) periodEnd.setHours(23, 59, 59, 999);

      if (periodEnd) {
        return testDate >= periodStart && testDate <= periodEnd;
      }
      return testDate >= periodStart;
    });
  };

  // Determine start and end day for this month based on month boundaries and today
  let startDay = 1;
  let endDay = totalDaysInMonth;

  if (year === currentDate.year && month === currentDate.month) {
    endDay = currentDate.day;
  } else if (year > currentDate.year || (year === currentDate.year && month > currentDate.month)) {
    // Future month
    endDay = 0;
    startDay = totalDaysInMonth + 1;
  }

  // 1. Fetch Previous Month's Balance (Opening Balance)
  let openingBalance = 0;
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;

  try {
    const prevMonthAttendance = await getMonthlyAttendance(guard.id, prevYear, prevMonth, companyId);
    // If the previous month has a recorded balance, use it as opening balance
    if (prevMonthAttendance.balance !== undefined) {
      openingBalance = prevMonthAttendance.balance;
    } else if (prevMonthAttendance.calculatedSalary !== undefined) {
      // Fallback if balance is not explicitly stored but calculation was done
      openingBalance = (prevMonthAttendance.calculatedSalary || 0) - (prevMonthAttendance.advanceSalary || 0);
    }
  } catch (error) {
    console.warn(`Could not fetch opening balance for guard ${guard.id} from ${prevYear}-${prevMonth}:`, error);
  }

  // 2. Count statuses
  let present = 0;
  let absent = 0;
  let leave = 0;
  let undeployed = 0;
  let ot = 0;

  for (let day = 1; day <= endDay; day++) {
    const dateKey = formatDate(year, month, day);
    const status = attendance.days[dateKey];

    // Check if deployed at all on this day
    const testDate = new Date(year, month - 1, day);
    testDate.setHours(0, 0, 0, 0);
    const isDeployed = deploymentPeriods.some((period) => {
      const periodStart = new Date(period.start);
      periodStart.setHours(0, 0, 0, 0);
      const periodEnd = period.end ? new Date(period.end) : null;
      if (periodEnd) periodEnd.setHours(23, 59, 59, 999);
      return testDate >= periodStart && (!periodEnd || testDate <= periodEnd);
    });

    if (!isDeployed) {
      undeployed++;
      continue;
    }

    // Check for OT on this day
    const isOTDay = otPeriods.some((period) => {
      const periodStart = new Date(period.start);
      periodStart.setHours(0, 0, 0, 0);
      const periodEnd = period.end ? new Date(period.end) : null;
      if (periodEnd) periodEnd.setHours(23, 59, 59, 999);
      return testDate >= periodStart && (!periodEnd || testDate <= periodEnd);
    });

    if (status === 'present') {
      present++;
      if (isOTDay) ot++;
    } else if (status === 'absent') {
      absent++;
    } else if (status === 'leave') {
      leave++;
      if (isOTDay) ot++;
    } else {
      present++;
      if (isOTDay) ot++;
    }
  }

  // 3. Calculate Pro-rated Salary
  const { finalSalary, currentMonthlySalary } = calculateProRatedSalary(
    guard,
    year,
    month,
    attendance.days,
    endDay
  );

  // Get advance salary from current month attendance document
  const advanceSalary = attendance.advanceSalary || 0;

  // Calculate balance: Opening Balance + Salary till today - Advances
  let balance = Math.round(openingBalance + finalSalary - advanceSalary);

  if (attendance.paymentStatus === 'paid' && attendance.balance !== undefined) {
    balance = attendance.balance;
  }

  return {
    totalDays: endDay,
    present,
    absent,
    leave,
    undeployed,
    ot,
    monthlySalary: currentMonthlySalary,
    salaryTillToday: finalSalary,
    advanceSalary,
    balance,
    openingBalance,
  };
};

/**
 * Get attendance history for a guard
 * Returns all monthly records
 */
export const getAttendanceHistory = async (
  guard: GuardData,
  companyId?: string
): Promise<AttendanceHistory[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const currentDate = getCurrentDateInfo();
  const history: AttendanceHistory[] = [];

  try {
    const monthsRef = collection(db, 'companies', uid, 'guardAttendance', guard.id, 'months');

    // Try to query with orderBy, but if it fails (no index), query without it
    let snapshot;
    try {
      const q = query(monthsRef, orderBy('year', 'desc'), orderBy('month', 'desc'));
      snapshot = await getDocs(q);
    } catch (error: any) {
      // If orderBy fails (likely missing index), query without it and sort in memory
      console.warn('OrderBy query failed, fetching without orderBy:', error);
      snapshot = await getDocs(monthsRef);
    }

    for (const doc of snapshot.docs) {
      const data = doc.data() as MonthlyAttendance;
      const totalDaysInMonth = getDaysInMonth(data.year, data.month);

      // For current month, only count days up to today
      // For past months, count all days in the month
      const isCurrentMonth = data.year === currentDate.year && data.month === currentDate.month;
      const daysToCount = isCurrentMonth ? currentDate.day : totalDaysInMonth;

      let present = 0;
      let absent = 0;
      let leave = 0;
      let ot = 0;

      // Collect deployment periods to check if deployed on each day
      const deploymentPeriods: { start: Date; end: Date | null }[] = [];
      const otPeriods: { start: Date; end: Date | null }[] = [];

      if (guard.currentDeployment?.postDate) {
        const start = parseDate(guard.currentDeployment.postDate);
        const end = parseDate(guard.currentDeployment.endDate);
        if (start) deploymentPeriods.push({ start, end });
      }

      if (guard.currentOTDeployment?.postDate) {
        const start = parseDate(guard.currentOTDeployment.postDate);
        const end = parseDate(guard.currentOTDeployment.endDate);
        if (start) {
          deploymentPeriods.push({ start, end });
          otPeriods.push({ start, end });
        }
      }

      if (guard.history && Array.isArray(guard.history)) {
        guard.history.forEach((h: any) => {
          const start = parseDate(h.postDate);
          const end = parseDate(h.endDate) || parseDate(h.completedAt);
          if (start) {
            deploymentPeriods.push({ start, end });
            if ((h.slotNumber || 0) > 100) {
              otPeriods.push({ start, end });
            }
          }
        });
      }

      // Count days up to today (for current month) or all days (for past months)
      for (let day = 1; day <= daysToCount; day++) {
        const testDate = new Date(data.year, data.month - 1, day);
        testDate.setHours(12, 0, 0, 0); // Use noon to avoid TZ edge cases
        const testTime = testDate.getTime();

        const isDeployed = deploymentPeriods.some((period) => {
          const s = new Date(period.start);
          s.setHours(0, 0, 0, 0);
          const e = period.end ? new Date(period.end) : null;
          if (e) e.setHours(23, 59, 59, 999);
          return testTime >= s.getTime() && (!e || testTime <= e.getTime());
        });

        if (!isDeployed) continue;

        const isOTDay = otPeriods.some((period) => {
          const s = new Date(period.start);
          s.setHours(0, 0, 0, 0);
          const e = period.end ? new Date(period.end) : null;
          if (e) e.setHours(23, 59, 59, 999);
          return testTime >= s.getTime() && (!e || testTime <= e.getTime());
        });

        const dateKey = formatDate(data.year, data.month, day);
        const status = data.days[dateKey];

        if (status === 'absent') {
          absent++;
        } else if (status === 'leave') {
          leave++;
          if (isOTDay) ot++;
        } else {
          // Default to present for all other cases within deployment
          present++;
          if (isOTDay) ot++;
        }
      }

      // 3. Calculate Pro-rated Salary
      const { finalSalary } = calculateProRatedSalary(
        guard,
        data.year,
        data.month,
        data.days,
        daysToCount
      );

      // Determine payment status
      let paymentStatus: 'paid' | 'unpaid' | 'current';
      if (isCurrentMonth) {
        paymentStatus = 'current';
      } else {
        // For past months, check if payment status is stored, default to 'unpaid'
        paymentStatus = data.paymentStatus || 'unpaid';
      }

      history.push({
        year: data.year,
        month: data.month,
        totalDays: daysToCount, // Show actual days counted (up to today for current month)
        present,
        absent,
        leave,
        ot,
        calculatedSalary: finalSalary,
        paymentStatus,
        advanceSalary: data.advanceSalary || 0,
        balance: data.balance !== undefined ? data.balance : (finalSalary - (data.advanceSalary || 0)),
        balanceReason: data.balanceReason,
      });
    }

    // Sort by year and month descending if we didn't use orderBy
    history.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });

    // Ensure current month is included even if no document exists yet
    const hasCurrentMonth = history.some(
      h => h.year === currentDate.year && h.month === currentDate.month
    );

    if (!hasCurrentMonth) {
      // Get current month attendance data
      try {
        const currentMonthAttendance = await getMonthlyAttendance(
          guard.id,
          currentDate.year,
          currentDate.month,
          companyId
        );

        const totalDaysInMonth = getDaysInMonth(currentDate.year, currentDate.month);
        const daysToCount = currentDate.day;

        let present = 0;
        let absent = 0;
        let leave = 0;

        for (let day = 1; day <= daysToCount; day++) {
          const dateKey = formatDate(currentDate.year, currentDate.month, day);
          const status = currentMonthAttendance.days[dateKey];

          if (status === 'present') {
            present++;
          } else if (status === 'absent') {
            absent++;
          } else if (status === 'leave') {
            leave++;
          } else {
            present++;
          }
        }

        // 3. Calculate Pro-rated Salary
        const { finalSalary } = calculateProRatedSalary(
          guard,
          currentDate.year,
          currentDate.month,
          currentMonthAttendance.days,
          daysToCount
        );

        // Add current month to history at the beginning
        history.unshift({
          year: currentDate.year,
          month: currentDate.month,
          totalDays: daysToCount,
          present,
          absent,
          leave,
          calculatedSalary: finalSalary,
          paymentStatus: 'current',
          advanceSalary: currentMonthAttendance.advanceSalary || 0,
          balance: currentMonthAttendance.balance !== undefined
            ? currentMonthAttendance.balance
            : (finalSalary - (currentMonthAttendance.advanceSalary || 0)),
          balanceReason: currentMonthAttendance.balanceReason,
        });
      } catch (error) {
        console.error('Error loading current month attendance:', error);
      }
    }

  } catch (error: any) {
    console.error('Error fetching attendance history:', error);
    // If there's an error, still try to include current month
    try {
      const currentMonthAttendance = await getMonthlyAttendance(
        guard.id,
        currentDate.year,
        currentDate.month,
        companyId
      );

      const totalDaysInMonth = getDaysInMonth(currentDate.year, currentDate.month);
      const daysToCount = currentDate.day;

      let present = 0;
      let absent = 0;
      let leave = 0;

      for (let day = 1; day <= daysToCount; day++) {
        const dateKey = formatDate(currentDate.year, currentDate.month, day);
        const status = currentMonthAttendance.days[dateKey];

        if (status === 'present') {
          present++;
        } else if (status === 'absent') {
          absent++;
        } else if (status === 'leave') {
          leave++;
        } else {
          present++;
        }
      }

      // Calculate salary - handle various formats
      let monthlySalary = 0;
      if (typeof guard.salary === 'number') {
        monthlySalary = guard.salary;
      } else if (typeof guard.salary === 'string') {
        const cleaned = guard.salary.replace(/[^0-9.]/g, '');
        monthlySalary = parseFloat(cleaned) || 0;
      } else if (guard.salary) {
        monthlySalary = parseFloat(String(guard.salary)) || 0;
      }

      // Validate salary is a valid number
      if (isNaN(monthlySalary) || monthlySalary < 0) {
        console.warn(`Invalid salary for guard ${guard.id}:`, guard.salary, 'defaulting to 0');
        monthlySalary = 0;
      }

      const calculatedSalary = totalDaysInMonth > 0 && monthlySalary > 0
        ? (monthlySalary / totalDaysInMonth) * (present + leave)
        : 0;

      // Validate result
      const finalSalary = isNaN(calculatedSalary) ? 0 : Math.round(calculatedSalary);

      history.push({
        year: currentDate.year,
        month: currentDate.month,
        totalDays: daysToCount,
        present,
        absent,
        leave,
        calculatedSalary: finalSalary,
        paymentStatus: 'current',
        advanceSalary: currentMonthAttendance.advanceSalary || 0,
        balance: currentMonthAttendance.balance !== undefined
          ? currentMonthAttendance.balance
          : (finalSalary - (currentMonthAttendance.advanceSalary || 0)),
        balanceReason: currentMonthAttendance.balanceReason,
      });
    } catch (innerError) {
      console.error('Error loading current month attendance:', innerError);
    }
  }

  return history;
};

/**
 * Get detailed salary breakdown for a guard in a specific month
 */
export const getMonthlySalaryBreakdown = async (
  guard: GuardData,
  year: number,
  month: number,
  companyId?: string
): Promise<SalaryBreakdownPeriod[]> => {
  const attendance = await getMonthlyAttendance(guard.id, year, month, companyId);
  const totalDaysInMonth = getDaysInMonth(year, month);
  const currentDate = getCurrentDateInfo();

  // Determine end day for calculation
  const isCurrentMonth = year === currentDate.year && month === currentDate.month;
  const endDay = isCurrentMonth ? currentDate.day : totalDaysInMonth;

  // Collect all deployment periods
  const deploymentPeriods: { start: Date; end: Date | null; salary?: number | string; client?: any; isOT?: boolean }[] = [];

  if (guard.currentDeployment?.postDate) {
    const start = parseDate(guard.currentDeployment.postDate);
    const end = parseDate(guard.currentDeployment.endDate);
    if (start) {
      deploymentPeriods.push({
        start,
        end,
        salary: guard.currentDeployment.salary,
        client: guard.currentDeployment.client
      });
    }
  }

  if (guard.currentOTDeployment?.postDate) {
    const start = parseDate(guard.currentOTDeployment.postDate);
    const end = parseDate(guard.currentOTDeployment.endDate);
    if (start) {
      deploymentPeriods.push({
        start,
        end,
        salary: guard.currentOTDeployment.salary,
        client: guard.currentOTDeployment.client,
        isOT: true
      });
    }
  }

  if (guard.history && Array.isArray(guard.history)) {
    guard.history.forEach((h: any) => {
      const start = parseDate(h.postDate);
      const end = parseDate(h.endDate) || parseDate(h.completedAt);
      if (start) {
        deploymentPeriods.push({
          start,
          end,
          salary: h.salary,
          client: h.client,
          isOT: (h.slotNumber || 0) > 100
        });
      }
    });
  }

  const breakdown: SalaryBreakdownPeriod[] = [];
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month - 1, totalDaysInMonth, 23, 59, 59, 999);

  deploymentPeriods.forEach(period => {
    const periodStart = new Date(period.start);
    periodStart.setHours(0, 0, 0, 0);

    const periodEnd = period.end ? new Date(period.end) : null;
    if (periodEnd) periodEnd.setHours(23, 59, 59, 999);

    // Intersection
    const effectiveStart = new Date(Math.max(periodStart.getTime(), monthStart.getTime()));
    const calculationEndBoundary = isCurrentMonth
      ? new Date(year, month - 1, currentDate.day, 23, 59, 59, 999).getTime()
      : monthEnd.getTime();

    const effectiveEnd = new Date(Math.min(
      periodEnd ? periodEnd.getTime() : Infinity,
      calculationEndBoundary
    ));

    if (effectiveStart > effectiveEnd) return;

    let presentDays = 0;
    const startDayNum = effectiveStart.getDate();
    const endDayNum = effectiveEnd.getDate();

    for (let d = startDayNum; d <= endDayNum; d++) {
      const dateKey = formatDate(year, month, d);
      const status = attendance.days[dateKey];
      // Default to present if not marked and within deployment period
      if (!status || status === 'present' || status === 'leave') {
        presentDays++;
      }
    }

    const salaryToParse = (period.salary !== undefined && period.salary !== 0)
      ? period.salary
      : guard.salary;

    let monthlySalary = 0;
    if (typeof salaryToParse === 'number') {
      monthlySalary = salaryToParse;
    } else if (typeof salaryToParse === 'string') {
      monthlySalary = parseFloat(salaryToParse.replace(/[^0-9.]/g, '')) || 0;
    }

    const dailyRate = totalDaysInMonth > 0 ? (monthlySalary / totalDaysInMonth) : 0;
    const calculatedSalary = Math.round(presentDays * dailyRate);

    const clientName = period.client?.name || (period.client?.branchName ? `${period.client.branchName}` : 'Undeployed / Unknown');
    const branchCode = period.client?.branchCode || period.client?.code || '—';

    breakdown.push({
      clientName,
      branchCode,
      startDate: effectiveStart.toLocaleDateString('en-GB'),
      endDate: effectiveEnd.toLocaleDateString('en-GB'),
      salary: monthlySalary,
      daysInMonth: totalDaysInMonth,
      dailyRate: Math.round(dailyRate),
      presentDays,
      calculatedSalary,
      isOT: period.isOT
    });
  });

  return breakdown;
};

/**
 * Get month name from month number
 */
export const getMonthName = (month: number): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1] || '';
};

/**
 * Clear all attendance data for all guards
 * This will delete all attendance records from the database
 * 
 * Firestore Route: companies/{companyId}/guardAttendance/{guardId}/months/{monthKey}
 */
export const clearAllAttendance = async (
  companyId?: string
): Promise<{ deletedGuards: number; deletedMonths: number }> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  console.log('clearAllAttendance: Starting deletion for company:', uid);
  console.log('Firestore Route: companies/' + uid + '/guardAttendance/{guardId}/months/{monthKey}');

  const results = {
    deletedGuards: 0,
    deletedMonths: 0,
  };

  try {
    // Get all guards
    const { loadAllGuardsForExport } = await import('../../manage_guard/service/guardService');
    const guards = await loadAllGuardsForExport(uid);

    console.log(`Found ${guards.length} guards to process`);

    // Process guards one by one to avoid batch size limits
    for (const guard of guards) {
      // Get all months for this guard
      const monthsRef = collection(db, 'companies', uid, 'guardAttendance', guard.id, 'months');
      const monthsSnapshot = await getDocs(monthsRef);

      if (monthsSnapshot.size > 0) {
        // Process months in batches (Firestore batch limit is 500)
        const BATCH_SIZE = 500;
        const monthDocs = monthsSnapshot.docs;

        for (let i = 0; i < monthDocs.length; i += BATCH_SIZE) {
          const batch = writeBatch(db);
          const monthBatch = monthDocs.slice(i, i + BATCH_SIZE);

          monthBatch.forEach((monthDoc) => {
            batch.delete(monthDoc.ref);
            results.deletedMonths++;
          });

          await batch.commit();
        }

        results.deletedGuards++;
        console.log(`Deleted ${monthsSnapshot.size} months for guard ${guard.id}`);
      }
    }

    console.log('clearAllAttendance completed:', results);
    return results;
  } catch (error: any) {
    console.error('clearAllAttendance error:', error);
    throw error;
  }
};

/**
 * Initialize today's attendance as 'present' for a guard if not already marked
 * This ensures the current month document exists in the database for history tracking
 */
export const initializeTodayAttendance = async (
  guardId: string,
  companyId?: string,
  guard?: GuardData
): Promise<void> => {
  const currentDate = getCurrentDateInfo();
  const todayDateKey = formatDate(currentDate.year, currentDate.month, currentDate.day);

  // Check if today is already marked
  const attendance = await getMonthlyAttendance(guardId, currentDate.year, currentDate.month, companyId);

  // If today is not marked, mark it as present (with guard data for salary calculation)
  if (!attendance.days[todayDateKey]) {
    await markAttendance(guardId, todayDateKey, 'present', companyId, guard);
  }
};

/**
 * Initialize today's attendance for multiple guards using batch writes (much faster)
 * This runs in the background and doesn't block the UI
 */
export const initializeTodayAttendanceBatch = async (
  guardIds: string[],
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) {
    console.error('initializeTodayAttendanceBatch: User not authenticated');
    throw new Error('User not authenticated');
  }

  const currentDate = getCurrentDateInfo();
  const todayDateKey = formatDate(currentDate.year, currentDate.month, currentDate.day);
  const monthKey = `${currentDate.year}-${String(currentDate.month).padStart(2, '0')}`;

  console.log('initializeTodayAttendanceBatch:', {
    guardCount: guardIds.length,
    companyId: uid,
    todayDateKey,
    monthKey
  });

  // Load guard data for salary calculation
  const { loadAllGuardsForExport } = await import('../../manage_guard/service/guardService');
  const allGuards = await loadAllGuardsForExport(uid);
  const guardMap = new Map(allGuards.map(g => [g.id, g]));

  // Firestore batch limit is 500 operations
  const BATCH_SIZE = 500;
  let totalUpdated = 0;

  try {
    // Process guards in batches
    for (let i = 0; i < guardIds.length; i += BATCH_SIZE) {
      const guardBatch = guardIds.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      let hasUpdates = false;

      // Read all documents in parallel first
      const docRefs = guardBatch.map(guardId =>
        doc(db, 'companies', uid, 'guardAttendance', guardId, 'months', monthKey)
      );

      console.log(`Processing batch ${Math.floor(i / BATCH_SIZE) + 1}, guards: ${guardBatch.length}`);

      const docSnaps = await Promise.all(docRefs.map(ref => getDoc(ref)));

      // Prepare batch writes with calculated fields
      for (let index = 0; index < docSnaps.length; index++) {
        const docSnap = docSnaps[index];
        const guardId = guardBatch[index];
        const docRef = docRefs[index];
        const guard = guardMap.get(guardId);

        if (docSnap.exists()) {
          const attendanceData = docSnap.data() as MonthlyAttendance;
          let needsUpdate = false;

          // Check if we need to initialize missing days up to today
          for (let day = 1; day <= currentDate.day; day++) {
            const dateKey = formatDate(currentDate.year, currentDate.month, day);
            if (!attendanceData.days[dateKey]) {
              attendanceData.days[dateKey] = 'present';
              needsUpdate = true;
            }
          }

          // Update if we added any days or if today wasn't marked
          if (needsUpdate || !attendanceData.days[todayDateKey]) {
            attendanceData.updatedAt = new Date();

            // Calculate and update summary fields if guard data is available
            if (guard) {
              const summary = await calculateMonthlySummary(guard, currentDate.year, currentDate.month, uid);
              attendanceData.presentDays = summary.present;
              attendanceData.absentDays = summary.absent;
              attendanceData.leaveDays = summary.leave;
              attendanceData.otDays = summary.ot;
              attendanceData.monthlySalary = summary.monthlySalary;
              attendanceData.salaryTillToday = summary.salaryTillToday;
            }

            batch.set(docRef, attendanceData, { merge: false });
            hasUpdates = true;
            totalUpdated++;
          }
        } else {
          // Create new document with all days up to today marked as present
          const attendanceData: MonthlyAttendance = {
            guardId,
            year: currentDate.year,
            month: currentDate.month,
            days: {},
            updatedAt: new Date(),
          };

          // Initialize all days up to today as 'present'
          for (let day = 1; day <= currentDate.day; day++) {
            const dateKey = formatDate(currentDate.year, currentDate.month, day);
            attendanceData.days[dateKey] = 'present';
          }

          // Calculate and add summary fields if guard data is available
          if (guard) {
            const summary = await calculateMonthlySummary(guard, currentDate.year, currentDate.month, uid);
            attendanceData.presentDays = summary.present;
            attendanceData.absentDays = summary.absent;
            attendanceData.leaveDays = summary.leave;
            attendanceData.otDays = summary.ot;
            attendanceData.monthlySalary = summary.monthlySalary;
            attendanceData.salaryTillToday = summary.salaryTillToday;
          }

          batch.set(docRef, attendanceData, { merge: false });
          hasUpdates = true;
          totalUpdated++;
        }
      }

      // Commit batch if there are updates
      if (hasUpdates) {
        console.log(`Committing batch with ${totalUpdated} updates`);
        await batch.commit();
        console.log(`Batch committed successfully`);
      } else {
        console.log('No updates needed for this batch');
      }
    }

    console.log(`initializeTodayAttendanceBatch completed: ${totalUpdated} guards updated`);
  } catch (error: any) {
    console.error('initializeTodayAttendanceBatch error:', {
      error,
      code: error.code,
      message: error.message,
      companyId: uid,
      guardCount: guardIds.length
    });
    throw error;
  }
};
